import React from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';

import Modal from '../../../components/modal/Modal';
import Button from '../../../../components/form/button/Button';
import Input from '../../../../components/form/input/Input';
import { Color } from '../../../../core/enums/Color';
import { spawnGlobalNotification } from '../../../../core/state/global-notifications/asyncActions';
import { GlobalNotificationTypes } from '../../../../core/enums/GlobalNotificationTypes';
import { getBarriers } from '../../../../core/state/barriers/asyncActions';
import { BarrierModeData } from '../../../../core/state/barriers/types';
import BarrierService from '../../../../core/services/BarrierService';
import { IP_REGEX } from '../../../../core/constants/regex';

import './EnableMaintenanceModeModal.scss';

interface Props {
  isOpen: boolean;
  closeModal: () => void;
  barrierId?: number;
}

const EnableMaintenanceModeModal: React.FC<Props> = ({ isOpen, closeModal, barrierId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const defaultFormValues = {
    ipAddress: '',
    port: ''
  };

  const validationSchema = yup
    .object()
    .shape({
      ipAddress: yup
        .string()
        .required(t('validation.generic.required'))
        .matches(IP_REGEX, t('validation.ip'))
        .matches(/^.{0,15}$/, t('validation.input.max15')),
      port: yup.number().required(t('validation.generic.required')).min(0, t('validation.port.min')).max(65536, t('validation.port.max'))
    })
    .required();

  const {
    handleSubmit,
    control,
    formState: { errors, isValid }
  } = useForm<BarrierModeData>({
    mode: 'onChange',
    defaultValues: defaultFormValues,
    resolver: yupResolver(validationSchema)
  });

  const maintenanceModeEnabledNotification = () => {
    dispatch(
      spawnGlobalNotification({
        type: GlobalNotificationTypes.Success,
        title: t('barrierManagement.maintenance.enabled.title'),
        message: t('barrierManagement.maintenance.enabled.text'),
        delay: 5000
      })
    );
  };

  const maintenanceModeCanceledNotification = () => {
    dispatch(
      spawnGlobalNotification({
        type: GlobalNotificationTypes.Info,
        title: t('common.changesDiscarded'),
        message: t('barrierManagement.barrier.unchanged'),
        delay: 5000
      })
    );
  };

  const onSubmit = async (formData: BarrierModeData) => {
    if (barrierId) {
      dispatch(
        spawnGlobalNotification({
          type: GlobalNotificationTypes.Warning,
          title: t('common.attention'),
          autoHide: false,
          buttons: {
            confirm: {
              text: t('common.proceed'),
              onClick: async () => {
                try {
                  await BarrierService.enableMaintenanceMode(barrierId, { ...formData });
                  maintenanceModeEnabledNotification();
                } catch (e) {
                  // Error handler
                }
              }
            },
            cancel: {
              text: t('common.cancel'),
              onClick: () => {
                maintenanceModeCanceledNotification();
              }
            }
          },
          closeButton: false,
          message: t('barrierManagement.barrier.modal.warning')
        })
      );

      closeModal();
      dispatch(getBarriers());
    }
  };

  const closeBarrierModal = () => {
    closeModal();
    maintenanceModeCanceledNotification();
  };

  const buttons = (
    <div className="modal-buttons">
      <Button color={Color.Blue} buttonTypes={['transparent']} onClick={closeBarrierModal}>
        <span>{t('common.cancel')}</span>
      </Button>
      <Button disabled={!isValid} form="enable-maintenance-mode" type="submit" buttonTypes={['blue']}>
        {t('common.enable')}
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      closeModal={closeBarrierModal}
      title={<h3 className="modal-title">{t('barrierManagement.action.enableMaintenanceMode')}</h3>}
      toolbar={buttons}
      size="small"
    >
      <form id="enable-maintenance-mode" className="enable-maintenance-mode-modal" onSubmit={handleSubmit(onSubmit)}>
        <div className="enable-maintenance-mode-modal__content">
          <Controller
            name="ipAddress"
            control={control}
            render={({ field: { onChange, value, name } }) => (
              <Input
                type="string"
                onChange={(e) => {
                  onChange(e.target.value);
                }}
                id="ipAddress"
                error={errors.ipAddress?.message}
                name={name}
                label="IP address*"
                placeholder="e.g. 192.158.1.38"
                value={value}
              />
            )}
          />
          <Controller
            name="port"
            control={control}
            render={({ field: { onChange, value, name } }) => (
              <Input
                className="enable-maintenance-mode-modal__port"
                label="Port*"
                placeholder="e.g. 20"
                id="port"
                error={errors.port?.message}
                name={name}
                type="string"
                value={value}
                onChange={(e) => onChange(e.target.value)}
              />
            )}
          />
        </div>
      </form>
    </Modal>
  );
};

export default EnableMaintenanceModeModal;
